import { AddressCard } from "@/components/AddressCard";
import { PageHeader } from "@/components/PageHeader";
import { StatCards } from "@/components/StatCards";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { getGreetingKey } from "@shared/analytics";
import api from "@shared/api/client";
import type { AddressProviderStatusCombo, AnalyticsSummary } from "@shared/types/analytics";
import { formatCurrency, groupBy } from "@shared/utils";
import { Box, Calendar } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as styles from "./DashboardPage.styles";

const DashboardPage = () => {
  const { t } = useTranslation(["common", "dashboard"]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [comboData, setComboData] = useState<AddressProviderStatusCombo[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryData, comboData] = await Promise.all([api.analytics.summary(), api.analytics.byAddressProviderStatus()]);
      setSummary(summaryData);
      setComboData(comboData);
    } catch {
      setSummary(null);
      setComboData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const greeting = useMemo(() => t(getGreetingKey(new Date().getHours())), [t]);

  const header = <PageHeader icon={Calendar} title={`${greeting}${user?.name ? `, ${user.name}` : ""}`} description={t("manageDescription")} />;

  if (loading) {
    return (
      <div className={styles.container}>
        {header}
        <div className={cn(styles.statsGrid, styles.pageEnter)}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={styles.skeletonCard}>
              <div className={styles.skeletonIcon} />
              <div className={styles.skeletonText} />
              <div className={styles.skeletonValue} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {header}

      {summary && <StatCards summary={summary} formatCurrency={formatCurrency} t={t} styles={styles} animationDelay={false} />}

      <div className={styles.comboSection}>
        <div className={styles.comboHeader}>
          <Box className={styles.comboIcon} />
          <h2 className={styles.comboTitle}>{t("dashboard:comboTitle")}</h2>
        </div>

        {!comboData.length ? (
          <div className={styles.comboEmpty}>
            <Box className={styles.comboEmptyIcon} />
            <p className={styles.comboEmptyText}>{t("dashboard:noData")}</p>
            <p className={styles.comboEmptyHint}>{t("dashboard:comboEmptyHint")}</p>
            <button className={styles.comboEmptyAction} onClick={() => navigate("/actions")}>
              {t("dashboard:viewActions")}
            </button>
          </div>
        ) : (
          <div className={styles.comboGrid}>
            {groupBy(comboData, (combo) => combo.address).map(([address, combos]) => (
              <AddressCard key={address} address={address} combos={combos} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
